import React from 'react';
import classNames from 'classnames';
import { useCombinedRefs } from '../../hooks/useCombinedRefs';
import FormHelperText, { FormHelperTextProps } from '../FormHelperText';

export interface TextareaProps {
  id?: string;
  row?: number;
  cols?: number;
  name: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode | string;
  errorText?: React.ReactNode | string;
  hasError?: boolean;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  charLimit?: number;
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  wrapperClassName?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    const {
      row,
      name,
      disabled,
      readOnly,
      placeholder,
      helperText,
      errorText,
      hasError = false,
      charLimit,
      formHelperTextProps,
      value = '',
      onBlur,
      onChange,
      wrapperClassName,
      className,
      ...otherProps
    } = props;
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = useCombinedRefs<HTMLTextAreaElement>(ref, textareaRef);
    const helperTextId = `${name}-helper-text`;
    const textareaClasses = classNames('ui-textarea', className, {
      'ui-textarea--disabled': disabled,
      'ui-textarea--error': hasError,
    });
    const hasHelperText = helperText || hasError || charLimit;
    let helperTextInner = hasError ? errorText : helperText;
    let hasFieldError = hasError;
    if (charLimit) {
      helperTextInner = `${value.length}/${charLimit}`;
      hasFieldError = hasError || value.length > charLimit;
    }

    const onInputBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (combinedRef && combinedRef.current) {
        combinedRef.current.scrollTop = 0;
      }

      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <div className={wrapperClassName}>
        <textarea
          id={name}
          name={name}
          rows={row}
          value={value}
          className={textareaClasses}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          onBlur={onInputBlur}
          aria-invalid={hasError}
          ref={combinedRef}
          aria-describedby={hasHelperText ? helperTextId : undefined}
          onChange={onChange}
          {...otherProps}
        />
        {hasHelperText && (
          <FormHelperText
            align={charLimit ? 'right' : 'left'}
            style={charLimit ? 'italic' : undefined}
            hasError={hasFieldError}
            helperTextId={helperTextId}
            text={helperTextInner}
            {...formHelperTextProps}
          />
        )}
      </div>
    );
  }
);

export default Textarea;
